import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND, SEGMENTS, HUB_TILES, RANK_STAGES, ECOSYSTEM_FLOW } from "../lib/data";

const Icon = ({ name, className }) => { const C = Icons[name] || Icons.Circle; return <C className={className} />; };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.05 } }) };

const IMG = {
  field: "https://images.unsplash.com/photo-1714830561739-11dfcc489cb1?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  farmer: "https://images.unsplash.com/photo-1696371269777-88d1ce71642c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  market: "https://images.unsplash.com/photo-1769598249913-9527a1fb151d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  artisan: "https://images.unsplash.com/photo-1640292343595-889db1c8262e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
};

const Overline = ({ children }) => <div className="text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">{children}</div>;

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-cream-100 text-soil-700 pt-24">
      {/* HERO */}
      <section className="relative overflow-hidden bharat-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-forest-500/30 bg-forest-100 px-4 py-1.5 mb-6">
              <Icons.Sparkles className="w-4 h-4 text-forest-600" />
              <span className="text-xs font-bold text-forest-700 uppercase tracking-wider">{BRAND.mall} · {BRAND.businessCenter}</span>
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] text-forest-800">
              जहाँ व्यापार, सेवा, खरीदारी और <span className="saffron-text">अवसर</span> — सब एक साथ जुड़ते हैं।
            </h1>
            <p className="mt-4 text-lg text-soil-600">Where business, services, shopping and opportunities come together — connecting local businesses, entrepreneurs and customers across Bharat.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/mall" data-testid="hero-mall-button"><Button className="forest-gradient text-cream-50 font-bold h-12 px-7 rounded-full text-base hover:opacity-90">Explore Genesis Mall</Button></Link>
              <Link to="/businesses" data-testid="hero-explore-button"><Button className="terracotta-gradient text-cream-50 font-bold h-12 px-6 rounded-full hover:opacity-90">Explore Our Businesses</Button></Link>
              <Link to="/opportunity" data-testid="hero-network-button"><Button variant="outline" className="border-forest-500/40 text-forest-700 hover:bg-forest-100 font-bold h-12 px-6 rounded-full">Join Genesis Network</Button></Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="grid grid-cols-2 gap-3">
            <img src={IMG.field} alt="Indian farmland at sunrise" loading="lazy" className="col-span-2 h-44 sm:h-56 w-full object-cover rounded-2xl border border-cream-300" />
            <img src={IMG.market} alt="Local Indian market vendor" loading="lazy" className="h-40 w-full object-cover rounded-2xl border border-cream-300" />
            <img src={IMG.artisan} alt="Indian artisan at work" loading="lazy" className="h-40 w-full object-cover rounded-2xl border border-cream-300" />
          </motion.div>
        </div>
      </section>

      <div className="weave-divider" />

      {/* ALL-IN-ONE HUB */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <Overline>All-in-One Business Center</Overline>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">One Platform. Many Businesses. <span className="saffron-text">Endless Possibilities.</span></h2>
          <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-9 gap-3 mt-10">
            {HUB_TILES.map((h, i) => (
              <motion.div key={h.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="card-cream rounded-2xl p-4 flex flex-col items-center gap-2 hover:-translate-y-1 transition-transform">
                <div className="w-10 h-10 rounded-xl bg-forest-100 flex items-center justify-center"><Icon name={h.icon} className="w-5 h-5 text-forest-600" /></div>
                <div className="text-xs font-semibold text-soil-700">{h.t}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* GENESIS GROUP ECOSYSTEM */}
      <section className="py-16 sm:py-20 bg-cream-200/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <Overline>Genesis Group</Overline>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">A growing ecosystem connecting businesses, people & opportunity</h2>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-8">
            {ECOSYSTEM_FLOW.map((step, i) => (
              <React.Fragment key={step}>
                <span className="px-4 py-2 rounded-full card-cream text-sm font-semibold text-soil-700">{step}</span>
                {i < ECOSYSTEM_FLOW.length - 1 && <Icons.ArrowRight className="w-4 h-4 text-terracotta-500" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS UNIVERSE */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center"><Overline>Our Business Universe</Overline><h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">Ten businesses. One ecosystem.</h2><p className="text-soil-600 mt-2">Each business has its own identity — tap any to enter its world.</p></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {SEGMENTS.map((s, i) => (
              <motion.div key={s.slug} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
                <Link to={`/businesses/${s.slug}`} data-testid={`segment-card-${s.slug}`} className="group block card-cream rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all h-full">
                  <div className="relative h-40 overflow-hidden">
                    <img src={s.img} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {s.star && <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded-full gold-gradient text-navy-900">★ STAR</span>}
                    {s.upcoming && <span className="absolute top-3 right-3 text-[10px] font-bold px-2 py-1 rounded-full bg-forest-800/80 text-cream-50">UPCOMING</span>}
                  </div>
                  <div className="p-5">
                    <div className="w-9 h-9 rounded-lg bg-forest-100 flex items-center justify-center mb-2 -mt-9 relative z-10 border-2 border-cream-50"><Icon name={s.icon} className="w-4 h-4 text-forest-600" /></div>
                    <h3 className="font-heading text-lg font-bold text-forest-800 group-hover:text-terracotta-600 transition-colors">{s.short}</h3>
                    <p className="text-soil-500 text-xs mt-1 line-clamp-2">{s.tagline}</p>
                    <div className="flex items-center gap-1.5 text-terracotta-600 text-sm font-semibold mt-3">Explore <Icons.ArrowRight className="w-4 h-4" /></div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* RURAL CONNECT TEASER */}
      <section className="py-16 sm:py-20 bg-forest-800 text-cream-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-saffron-500 mb-3">Rural Connect</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-cream-50">Connecting Bharat's local enterprise with modern markets</h2>
            <p className="text-cream-200/80 mt-4 max-w-xl">Built around the idea of connecting local enterprise — farmers, artisans, MSMEs and neighbourhood retailers — with wider opportunities and a modern digital platform.</p>
            <div className="flex flex-wrap gap-2 mt-6">{["Support Local", "Connect Local", "Grow Local", "Think Global"].map((t) => <span key={t} className="text-sm px-3 py-1.5 rounded-full bg-white/10 text-cream-100">{t}</span>)}</div>
            <Link to="/rural-connect" data-testid="home-rural-cta" className="inline-block mt-7"><Button className="terracotta-gradient text-cream-50 font-bold h-11 px-6 rounded-full">Discover Rural Connect</Button></Link>
          </div>
          <img src={IMG.farmer} alt="Indian farmer in field" loading="lazy" className="h-72 w-full object-cover rounded-3xl border border-white/10" />
        </div>
      </section>

      {/* GROW WITH GENESIS */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <Overline>Genesis Business Network</Overline>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-forest-800">Grow With Genesis</h2>
          <p className="text-soil-600 mt-2 max-w-2xl mx-auto">A transparent, compliant network opportunity — refer, build a team, progress ranks and unlock rewards.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
            {RANK_STAGES.map((st, i) => (
              <motion.div key={st.k} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="card-cream rounded-2xl p-5">
                <div className="w-10 h-10 mx-auto rounded-full forest-gradient flex items-center justify-center font-mono-fin font-bold text-cream-50 mb-3">{i + 1}</div>
                <div className="font-heading text-lg font-extrabold text-terracotta-600">{st.k}</div>
                <div className="text-soil-500 text-xs mt-1">{st.d}</div>
              </motion.div>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-9">
            <Link to="/ranks-rewards" data-testid="home-ranks-cta"><Button className="forest-gradient text-cream-50 font-bold h-11 px-6 rounded-full">See Ranks &amp; Rewards</Button></Link>
            <Link to="/register" data-testid="home-join-cta"><Button variant="outline" className="border-terracotta-500/40 text-terracotta-600 hover:bg-terracotta-100 h-11 px-6 rounded-full">Join the Ecosystem</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
